import React, { useEffect, useState, useRef } from 'react';

import { Carousel } from '@trqk/components';
import { animateScrollLeft } from '@trqk/utils';

import { WelcomeCard } from './components';

import { INSIGHTS_TOUR_CARD_IDS } from '../../../../../constants';
import { getSlideConfigs } from './helpers';
import { usePaymentPlanStatus } from '../../../../../hooks';

export const scrollDuration = 1000;

/**
 * @typedef {TourCardsProps}
 * @property {InsightStatementHistoryData[] | null} statementHistoryData data sourced from the
 * getTrqkInsightsForUser query
 * @property {boolean} queryCalled whether the getTrqkInsightsForUser query was called
 * @property {boolean} queryLoading whether the getTrqkInsightsForUser query is loading
 */
interface TourCardsProps {
    detectionsActive: boolean;
    entityId: string;
    statementHistoryData: InsightStatementHistoryData[] | null;
    queryCalled: boolean;
    queryLoading: boolean;
    userId: string;
}

/**
 * Uses a Carousel component to render the Insights "tour" cards in a horizontal scrolling div with
 * scroll controls. Auto-scrolls to show the first un-checked tour card if possible.
 * @param {props} TourCardsProps
 */
export const TourCards = ({
    detectionsActive,
    entityId,
    statementHistoryData,
    queryCalled,
    queryLoading,
    userId,
}: TourCardsProps): JSX.Element => {
    const { isPublisherEdition } = usePaymentPlanStatus();
    const slideConfigs = getSlideConfigs({
        detectionsActive,
        isPublisherEdition,
        statementHistoryData,
        entityId,
        userId,
    });
    const slides: Slide[] = [
        {
            content: <WelcomeCard />,
            id: INSIGHTS_TOUR_CARD_IDS.welcome,
        },
    ].concat(
        slideConfigs.map(
            ({
                Component,
                checked,
                step,
                id,
                localStorageKey,
            }: TourCardSlideConfig): Slide => ({
                id,
                content: (
                    <Component
                        checked={checked}
                        step={step}
                        localStorageKey={localStorageKey}
                    />
                ),
            }),
        ),
    );

    const idToReveal =
        slideConfigs.find(
            ({ checked }: TourCardSlideConfig): boolean => !checked,
        )?.id ?? null;
    const [didRevealNextStep, setDidRevealNextStep] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Scroll to the first un-checked tour card when the insights data has loaded. Do this only
        // once per mount cycle.
        if (
            idToReveal !== null &&
            !didRevealNextStep &&
            queryCalled &&
            !queryLoading
        ) {
            if (wrapperRef.current) {
                const wrapper = wrapperRef.current;
                const wrapperBox = wrapper.getBoundingClientRect();
                const cardToReveal = document.getElementById(idToReveal);

                if (cardToReveal) {
                    const cardBox = cardToReveal.getBoundingClientRect();
                    const leftSpace = cardBox.left - wrapperBox.left;
                    const maxScroll = wrapper.scrollWidth - cardBox.left;

                    const amountToMoveLeft = Math.min(leftSpace, maxScroll);

                    if (amountToMoveLeft > 0) {
                        animateScrollLeft(
                            wrapper,
                            'easeInOutQuad',
                            amountToMoveLeft,
                            scrollDuration,
                        );

                        setDidRevealNextStep(true);
                    }
                }
            }
        }
    }, [idToReveal, didRevealNextStep, queryCalled, queryLoading, wrapperRef]);

    return <Carousel ref={wrapperRef} slides={slides} />;
};
